import { ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import { useQuery } from '@apollo/client';
import { Router as ReactRouter, Route, Switch } from 'react-router-dom';
import { useConditionalEffect, usePrevious } from 'react-delta';

import * as K from 'common/constants';
import { labsState } from 'components/account/account.state';
import { setUITheme } from 'service/browser';
import history from 'service/customHistory';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

// queries
import { TEAM_TOKEN_QUERY } from 'common/queries/apolloQueries';

// components
import MainNavBar from 'components/other/MainNavBar/MainNavBar';
import TeamSwitchDialog from 'components/account/TeamSwitchDialog/TeamSwitchDialog';
import PageLevelErrorBoundary from 'components/common/ModernUIErrorBoundary/ModernUIErrorBoundary';
import LayoutStateManager from 'components/common/Managers/LayoutStateManager';
import Router from 'components/common/Router/Router';
import { DebugManager } from 'components/common/Managers/DebugManager';
import RouteStateManager from 'components/common/Managers/RouteStateManager';
import SettingsManager from 'components/common/Managers/SettingsManager';
import { settingsState, themeState } from 'components/common/Managers/SettingsManager.state';
import { accountDataState } from 'components/account/account.state';
import PreferencesManager from 'components/common/Managers/PreferencesManager';
import FacetsManager from 'components/common/Managers/FacetsManager';
import PendoManager from 'components/common/Managers/PendoManager';
import WebVitalsManager from 'components/common/Managers/WebVitalsManager';
import S1ConsoleUserManager from 'components/common/Managers/S1ConsoleUserManager';

import { enablePerformanceLogger, setMark } from 'common/PerformanceLogger';

import './styles/app.scss';

interface SwitchProps {
  location: { pathname: string };
  children: ReactNode;
  // eslint-disable-next-line
  stateCapture: Record<string, any>;
}

// eslint-disable-next-line
let prevStateCapture: Record<string, any> = {};
function SwitchSpy(props: SwitchProps): JSX.Element {
  const {
    children,
    stateCapture,
    location: { pathname },
  } = props;

  const updatedState: Array<string> = [];
  Object.entries(stateCapture).forEach(([k, v]) => {
    if (v === undefined && prevStateCapture[k] === undefined) return;
    if (k in prevStateCapture && prevStateCapture[k] === v) return;
    updatedState.push(k);
  });
  prevStateCapture = stateCapture;
  setMark('routeSwitch', { pathname, updatedState });
  return <Switch>{children}</Switch>;
}

function App() {
  const { data } = useQuery(TEAM_TOKEN_QUERY);
  const teamTokenMismatch = data.ClientAccountState.teamTokenMismatch;
  const isTeamTokenMismatch = !!teamTokenMismatch?.actual;

  const settings = useRecoilValue(settingsState);
  const labs = useRecoilValue(labsState);
  const accountData = useRecoilValue(accountDataState);
  const { isSingularityXDRUser } = accountData;

  // Perf logger
  const isPerformanceLoggerEnabled = useIsLabEnabled(K.LAB.PERFORMANCE_LOGGER);
  if (isPerformanceLoggerEnabled) {
    enablePerformanceLogger();
  }

  const { reactPaths, reactPathsBlacklist } = settings;
  window.reactPaths = reactPaths;
  window.reactPathsBlacklist = reactPathsBlacklist;

  const theme = useRecoilValue(themeState);
  const prevTheme = usePrevious(theme);
  useConditionalEffect(() => {
    setUITheme(theme);
  }, theme !== prevTheme);

  const teamSwitchDialog = (): ReactNode => {
    if (teamTokenMismatch) {
      return (
        <TeamSwitchDialog
          message={teamTokenMismatch.message}
          actual={teamTokenMismatch.actual}
          requested={teamTokenMismatch.requested}
          showModal={isTeamTokenMismatch}
        />
      );
    }
    return null;
  };

  const stateCapture = {
    teamToken: data,
    settings,
    labs,
  };

  return (
    <div className="App">
      <LayoutStateManager />
      <PendoManager />
      <DebugManager />
      <ReactRouter history={history}>
        <RouteStateManager />
        <SettingsManager />
        {isSingularityXDRUser && <S1ConsoleUserManager />}
        <PreferencesManager />
        <FacetsManager />
        <WebVitalsManager />
        <MainNavBar isTeamTokenMismatch={isTeamTokenMismatch} />
        {teamSwitchDialog()}
        <PageLevelErrorBoundary>
          <SwitchSpy location={history.location} stateCapture={stateCapture}>
            <Route>
              <Router />
            </Route>
          </SwitchSpy>
        </PageLevelErrorBoundary>
      </ReactRouter>
    </div>
  );
}

export default App;
